import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { Input, FormGroup } from "reactstrap";
import http from "../../../utils/http-common";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import { getPublicUrl } from "../../../utils/Utils";
import { findUpper } from "../../../utils/Utils";
import swal from "sweetalert";

const TYPE_TABS = [
  { key: "", label: "All" },
  { key: "solo", label: "Solo" },
  { key: "clinic", label: "Clinic" },
  { key: "hospital", label: "Hospital" },
];

const STATUS_OPTS = [
  { value: "", label: "All statuses" },
  { value: "visible", label: "Visible" },
  { value: "to_verify", label: "To verify" },
  { value: "incomplete", label: "Incomplete" },
];

function statusBadge(status) {
  if (status === "visible") {
    return { label: "Visible", bg: "#dcfce7", color: "#166534" };
  }
  if (status === "to_verify") {
    return { label: "To verify", bg: "#ffedd5", color: "#c2410c" };
  }
  return { label: "Incomplete", bg: "#fff", color: "#475569", border: "1px solid #cbd5e1" };
}

/**
 * Global practitioners directory — derived Visible / To verify / Incomplete.
 */
const PractitionersDirectory = () => {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({ all: 0, solo: 0, clinic: 0, hospital: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 40;

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [qDebounced, type, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, hideTest: "1" };
      if (qDebounced) params.q = qDebounced;
      if (type) params.type = type;
      if (status) params.status = status;
      const res = await http.get("/api/v1/admin/practitioners", {
        params,
        suppressInlineErrorSwal: true,
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total) || 0);
      setTypeCounts(res.data?.typeCounts || { all: 0, solo: 0, clinic: 0, hospital: 0 });
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not load practitioners"), "error");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, qDebounced, type, status]);

  useEffect(() => {
    load();
  }, [load]);

  const openFiche = (row) => {
    if (!row?.id) return;
    history.push(getPublicUrl(`teleconsultations/practitioner-approvals?id=${encodeURIComponent(row.id)}`));
  };

  const countFor = (key) => {
    if (!key) return typeCounts.all || 0;
    return typeCounts[key] || 0;
  };

  return (
    <>
      <Head title="Practitioners" />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle page>Practitioners</BlockTitle>
            <BlockDes>
              <p>All doctors on the platform.</p>
            </BlockDes>
          </BlockHeadContent>
        </BlockHead>

        <Block>
          <div className="admin-practitioners-filters">
            <FormGroup className="admin-practitioners-filters__search">
              <div className="form-control-wrap">
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Search a practitioner"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </FormGroup>
            <FormGroup className="admin-practitioners-filters__status">
              <Input
                type="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Status"
              >
                {STATUS_OPTS.map((o) => (
                  <option key={o.value || "all"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>

          <div className="admin-practitioners-type-tabs" role="tablist" aria-label="Practice type">
            {TYPE_TABS.map((tab) => {
              const active = type === tab.key;
              return (
                <button
                  key={tab.key || "all"}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setType(tab.key)}
                  className="btn btn-sm"
                  style={{
                    borderRadius: 999,
                    border: active ? "none" : "1px solid #e2e8f0",
                    background: active ? "#2563eb" : "#fff",
                    color: active ? "#fff" : "#334155",
                    fontWeight: 500,
                    padding: "6px 14px",
                  }}
                >
                  {tab.label}{" "}
                  <span style={{ opacity: active ? 0.9 : 0.65 }}>({countFor(tab.key)})</span>
                </button>
              );
            })}
          </div>

          <div className="admin-practitioners-list">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-soft text-center py-4 mb-0">No practitioners match these filters.</p>
            ) : (
              rows.map((row) => {
                const badge = statusBadge(row.status);
                const initials = findUpper(row.name || "?");
                return (
                  <div key={row.id} className="admin-practitioners-list__row">
                    <span className="admin-practitioners-list__avatar" aria-hidden>
                      {initials}
                    </span>
                    <div className="admin-practitioners-list__body">
                      <div className="admin-practitioners-list__name text-truncate">{row.name}</div>
                      <div className="admin-practitioners-list__meta text-truncate">
                        {row.specialty || "—"}
                        <span className="mx-1">·</span>
                        {row.affiliation}
                      </div>
                    </div>
                    <span
                      className="admin-practitioners-list__badge"
                      style={{
                        background: badge.bg,
                        color: badge.color,
                        border: badge.border || "none",
                      }}
                    >
                      {badge.label}
                    </span>
                    <button
                      type="button"
                      className="btn btn-icon btn-trigger flex-shrink-0"
                      aria-label={`Open ${row.name}`}
                      onClick={() => openFiche(row)}
                    >
                      <Icon name="eye" />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {total > limit ? (
            <div className="mt-3">
              <PaginationComponent
                itemPerPage={limit}
                totalItems={total}
                paginate={setPage}
                currentPage={page}
              />
            </div>
          ) : null}
        </Block>
      </Content>
    </>
  );
};

export default PractitionersDirectory;
